import { ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { HTML5AdParameterBuilder } from 'tag/ts/tag/ads/freewheel/builders/HTML5AdParameterBuilder';
import { IAdParameters } from 'tag/ts/tag/ads/freewheel/interfaces/IAdParameters';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { AbstractResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractResourceConfigBuilder';
import { STVPlatformConfigs } from 'tag/ts/tag/player/stv/configs/STVPlatformConfigs';
import { STVUtilities } from 'tag/ts/tag/utilities/STVUtilities';
import { TagContentUtilities } from 'tag/ts/tag/utilities/TagContentUtilities';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Base class for Resource Configuration builder classes for HTML5
 */
export abstract class AbstractHTML5ResourceConfigBuilder extends AbstractResourceConfigBuilder {

	protected readonly html5ExcludedFMSKeys: string[];
	#contentUtilities: TagContentUtilities;

	protected constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'AbstractHTML5ResourceConfigBuilder',
				...argument
			} );

		this.#contentUtilities = new TagContentUtilities( { tag: this.tag } );
		this.html5ExcludedFMSKeys = [
			'correlator',
			'imafw__fw_app_bundle',
			'imafw__fw_h_referer',
			'imafw_cust_params',
			'imafw_description_url',
			'imafw_ifa',
			'imafw_nextShowsIdsArray',
			'imafw_rdid',
			'imafw_streamingUrl',
			'imafw_playername_version',
			'imafw__fw_did',
			'imafw_fms_userid',
			'imafw_npa',
			...( this.#contentUtilities?.isKidsContent() ? [ 'imafw_sub' ] : [] )
		];
	}

	/**
	 * @description Returns filled resource configuration for playback for HTML5
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve, reject ) => {

				// create instance of resource config
				const resource: ResourceConfigurationInterface = window.avia.createResourceConfig();
				this?.logger?.log( 'getResult', 'created instance of ResourceConfigurationInterface using avia:', resource );

				// set values for HTML5 playback
				const promiseArray: { ( ...args ): Promise<any> }[] = [
					// IMPORTANT: must call getSiteSessionData to initialize siteSessionData before accessing it via getter: this.tag.model.siteUtilities.siteSessionData
					() => this.tag.model.siteUtilities.getSiteSessionData( this.name ), // load siteSessionData
					() => this.setStartBitrate( resource ), // bitrate setting
					() => this.setContextMetadata( resource ), // set resource metadata for tracking
					() => this.setOverrides( resource ) // set resource overrides
				];

				this.utils.Function.runPromiseArray( promiseArray ).then(
					() => {
						resolve( {
							msg: 'Resource config',
							resource: resource
						} );
					}
				).catch(
					( error ) => {
						// HTML5 resource config steps failed, but provide fallback with basic resource config
						this?.logger?.warn( 'HTML5 resource config setup failed, using basic configuration:', error );
						resolve( {
							msg: 'Resource config (basic)',
							resource: resource
						} );
					}
				);
			}
		);
	}

	/**
	 * @description Sets resource metadata for tracking
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected setContextMetadata = ( resource: ResourceConfigurationInterface ): Promise<ResourceConfigurationInterface> => {
		/**
		 * @description Conviva specific device + framework parameters. For STV this should be set dynamically. Setting to 'HTML5' for now
		 * @param {ResourceConfigurationInterface} resource
		 * @returns {ResourceConfigurationInterface}
		 */
		const setDeviceFramework = ( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface => {
			resource.metadata.deviceCategory = 'HTML5';
			resource.metadata.frameworkName = 'SmartTag';
			resource.metadata.frameworkVersion = window?.PlayerServicesProjectInfo?.release_version;
			return resource;
		};

		return new Promise(
			( resolve, reject ) => {
				// sanity check + instantiation of metadata object
				if ( resource.metadata === undefined ) resource.metadata = {};

				// if incoming context metadata is a non-empty object
				if (
					!this.utils.Type.isEmpty( this.params?.CONTEXT?.metadata )
					&&
					this.utils.Type.isObject( this.params?.CONTEXT?.metadata )
				) {
					// add ALL context metadata properties to resource.metadata without parsing and processing
					// deep merge to prevent site from modifying metadata after assignment and to retain all values including 0, null, false
					// NOTE: We intentionally preserve 0, null, undefined values here. It is Tealium's responsibility to handle
					// these values appropriately on their end after metadata is passed to avia player and tracking services.
					resource.metadata = this.utils.DeepMerge.deepmerge(
						resource.metadata,
						this.params?.CONTEXT?.metadata
					);
				}

				// set device + framework
				resource = setDeviceFramework( resource );

				// additional processing per asset type
				switch ( this.tag.model.contentUtilities.assetEmbedType( this.params ) ) {
					case AssetEmbedType.ASSET_URL:
					case AssetEmbedType.PID:
						resource.metadata = this.apiMetadata;
						break;
					case AssetEmbedType.CID:
						// do nothing
						break;
					default:

				}

				this?.logger?.log( 'setContext:', resource );
				// resolve promise returning altered resource config
				resolve( resource );
			}
		);
	};

	/**
	 * @description Sets platform specific resource for HTML5 app
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setPlatformSpecificResource = ( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface => {
		const platformSpecificResourceConfig = new STVPlatformConfigs( { tag: this.tag } );
		this?.logger?.log( 'setPlatformSpecificResource', 'Platform Configs:', platformSpecificResourceConfig.resourceConfig );
		// merge it to resource config
		resource = this.utils.DeepMerge.deepmerge(
			resource,
			platformSpecificResourceConfig.resourceConfig
		);
		return resource; // return resource config
	};

	/**
	 * @description Determines and sets any required values for ad playback
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setAdParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		resource = this.setViewabilityTracking( resource );
		resource = this.setSsaiParameters( resource );
		// merge incoming ad settings data
		resource = this.mergeParamsAdSettings( resource );
		return resource;
	}

	/**
	 * Sets ssai parameters in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setSsaiParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();

		// remove ssai if VODFather is enabled but cannot set valid contentSourceId
		if (
			this.tag?.vodFatherUtilities?.isEnabled() &&
			this.utils.Type.isEmpty( this.tag?.vodFatherUtilities?.getDaiCmsId() )
		) {
			this.logger?.warn( 'setSsaiParameters:', 'VODFather is enabled but daiCmsId is unset, contentSourceId cannot be set... removing ssai from resource' );
			resource = this.removeSsai( resource );
			return resource;
		}

		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.DAI_API ) {
			resource = this.setDaiApiAdParameters( resource );
			// enable nonce for INTL when using DAI
			resource = this.setEnableNonce( resource, this.tag.tagUtilities.isInternationalContext( this.tag ) );
		}

		// html5 specific ad parameters
		if ( this?.tag?.model?.config?.tag?.ResourceConfigBuilder?.params?.ads?.enabled ) {
			resource = this.setHTML5AdParameters( resource );
		}

		// process millstone use case adding daiParams from millstone metadata
		resource = this.setMillstoneDAIParams( resource );

		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.DAI_API ) {
			resource = this.normalizeDaiApiDidInputs( resource );
		}

		// CLID3-only (linear ad tracking) support: configure CLID3 as the primary ad adapter
		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.LINEAR_AD_TRACKING ) {
			resource = this.handleLinearAdTrackable( resource );
		}
		return resource;
	}

	/**
	 * @description Normalizes _fw_did related inputs for CTV DAI API streams.
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected normalizeDaiApiDidInputs( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const ctvFms = this?.params?.AD?.FW?.FMS;

		const ctvLatValue =
			ctvFms?.imafw_is_lat ||
			ctvFms?.is_lat ||
			ctvFms?.imafw__fw_is_lat;
		const latEnabled: boolean = String( ctvLatValue ) === '1';
		// default lat value to 0 if not set
		const latValue: string = latEnabled ? '1' : '0';

		// all lat values should match
		resource = this.setSsaiAdParameter( resource, 'is_lat', latValue );
		resource = this.setSsaiAdParameter( resource, 'imafw_is_lat', latValue );
		resource = this.setSsaiAdParameter( resource, 'imafw__fw_is_lat', latValue );

		const idtype = ctvFms?.imafw_idtype || ctvFms?.idtype;
		const rdid = ctvFms?.rdid || ctvFms?.imafw_rdid;

		// remove to ensure correct behavior below
		if ( !this.utils.Type.isEmpty( resource?.ad?.ssai?.adParameters?._fw_did ) ) {
			delete resource.ad.ssai.adParameters._fw_did;
		}

		// For LAT ON cases omit _fw_did related params
		if ( latEnabled ) {
			return resource;
		}

		if ( !this.utils.Type.isEmpty( idtype ) ) {
			resource = this.setSsaiAdParameter( resource, 'imafw_idtype', idtype );
		}
		if ( !this.utils.Type.isEmpty( rdid ) ) {
			resource = this.setSsaiAdParameter( resource, 'rdid', rdid );
		}
		if ( !this.utils.Type.isEmpty( idtype ) && !this.utils.Type.isEmpty( rdid ) ) {
			resource = this.setSsaiAdParameter( resource, '_fw_did', `${idtype}:${rdid}` );
		}
		return resource;
	}

	/**
	 * @description Sets HTML5 specific ad parameters
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 * @protected
	 */
	protected setHTML5AdParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const adParameterBuilder = new HTML5AdParameterBuilder( { tag: this.tag } );
		const adParameters: IAdParameters = adParameterBuilder.buildAdParameters();
		const { custParams, ...ssaiAdParameters } = adParameters;

		// set resource.ad.custParams
		resource = this.setAdCustParams( resource, custParams );

		// set resource.ad.ssai.adParameters
		if ( !this.utils.Type.isEmpty( ssaiAdParameters ) ) {
			this.logger.log( 'setHTML5AdParameters', 'Setting HTML5 specific ad parameters', 'Params:', ssaiAdParameters );
			Object.keys( ssaiAdParameters ).forEach( ( key ) => {
				if ( !this.utils.Type.isEmpty( ssaiAdParameters[ key ] ) ) {
					resource = this.setSsaiAdParameter( resource, key, ssaiAdParameters[ key ] );
				} else {
					this.logger.warn(
						'setHTML5AdParameters', 'Filtering out ad parameter with unset value...', 'param:', key );
				}
			} );
		}
		return resource;
	}

	/**
	 * @description Determines and sets any required values for ad playback via DAI API
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setDaiApiAdParameters( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const stvUtilities = STVUtilities.getInstance();
		const adSetupInfo = this.tag?.model?.adUtilities?.adSetupInfo();
		const fwDaiApiRequest = this.createFreewheelRequest( adSetupInfo );

		const isContentLive = this.tag.model.contentUtilities.isLive(
			this.params,
			this?.tag?.model?.apiMetadata
		);
		const contentAssetKey = this.tag.model.contentUtilities.getDAIAssetKey(
			this.params,
			this?.tag?.model?.apiMetadata
		);

		// INFO: Set values for VOD+DAIAPI and LIVE+DAIAPI test harness for HTML5 app
		resource = this.setApiKey( resource, '7j3n273bfj7jcnvf22d713a51f' );
		resource = this.setAssetKey( resource, isContentLive ? contentAssetKey : null );
		resource = this.setAuthToken( resource, null );

		resource = this.setContentSourceId( resource );

		// set videoId to contentId (not pid) from apiMetadata
		resource = this.setVideoId( resource, isContentLive ? null : this?.tag?.model?.apiMetadata?.contentId );
		resource = this.setFilterOutPremiumAudioCodecs( resource, isContentLive ? true : stvUtilities.canFilterOutPremiumAudioCodecs( this?.params?.SYSTEM?.device ) );
		resource = this.setIsLive( resource, false );
		resource = this.setUseApi( resource, true );
		resource = this.setFormat( resource, stvUtilities.getFormat( this?.tag?.model?.apiMetadata ) );

		if ( !this.utils.Type.isEmpty( fwDaiApiRequest.parameters ) ) {
			// add all params to adParameters, add bundle to adParameters.cust_params
			this?.logger?.log( 'setDaiApiAdParameters', 'setting Freewheel parameters as SSAI ad parameters...', 'Freewheel params:', fwDaiApiRequest.parameters );
			fwDaiApiRequest.parameters
				.filter( ( param ) => {
					if ( this.utils.Type.isEmpty( param.value ) ) {
						this?.logger?.warn( 'setDaiApiAdParameters', 'filtering out Freewheel param with unset value...', 'param:', param );
						return false;
					}
					if ( this.html5ExcludedFMSKeys.includes( param.key ) ) {
						this?.logger?.warn( 'setDaiApiAdParameters', 'filtering out excluded HTML5 param...', 'param:', param );
						return false;
					}
					return true;
				} )
				.forEach( ( param ) => resource = this.setSsaiAdParameter( resource, param.key, param.value ) );
		}
		return resource;
	}

	/**
	 * @description Sets contentSourceId as SSAI parameter. Determines value depending on whether VODFather is enabled.
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setContentSourceId( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if ( this.tag?.vodFatherUtilities?.isEnabled() ) {
			const daiCmsId = this.tag?.vodFatherUtilities?.getDaiCmsId();
			this.logger?.log( 'setContentSourceId', 'VODFather enabled, setting contentSourceId to daiCmsId:', daiCmsId );
			return super.setContentSourceId( resource, daiCmsId );
		}
		this.logger?.log( 'setContentSourceId', 'VODFather disabled, setting contentSourceId to AdOps constant:', this.TagConstants?.ADOPS_ID?.CMSID );
		return super.setContentSourceId( resource, this.TagConstants?.ADOPS_ID?.CMSID );
	}

	/**
	 * @description Processes resource for CLID3-only (linear ad tracking) setup
	 * @param {IResourceConfig} resource
	 * @returns {IResourceConfig}
	 */
	protected handleLinearAdTrackable( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		// Add resource changes for CLID3-only (linear ad tracking)
		if ( !this.tag.model.contentUtilities.isLinearAdTrackable() ) {
			this?.logger?.log( 'handleLinearAdTrackable', 'isLinearAdTrackable=false... returning resource unmodified' );
			return resource;
		} else {
			this?.logger?.log( 'handleLinearAdTrackable', 'isLinearAdTrackable=true... set CLID3 as ad adapter, remove CSAI' );
			if ( this.utils.Type.isEmpty( resource?.ad ) ) {
				resource.ad = {};
			}

			// configure CLID3 as the primary ad adapter (per Avia CLID3 docs)
			if ( this.utils.Type.isEmpty( resource?.ad?.adapter ) ) {
				resource.ad.adapter = {};
			}
			resource.ad.adapter.id = 'clid3';

			// remove CSAI and SSAI when CLID3 is the adapter.
			resource = this.removeCsai( resource );
			resource = this.removeSsai( resource );
			return resource;
		}
	}

	/**
	 * @description Sets viewabilityTracking in resource config. Sets only partner in tracking object.
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setViewabilityTracking( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		const deviceType = this?.params?.SYSTEM?.device;
		const stvUtilities = STVUtilities.getInstance();
		const trackingObj = {
			partner: `pplus_${stvUtilities.getPlatformEnvToId( deviceType )}_app`
		} as any;
		super.setViewabilityTracking( resource, trackingObj );

		return resource;
	}

	/**
	 * @description Deep merges params.AD.SETTINGS into resource.ad
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected mergeParamsAdSettings( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		if (
			this.utils.Type.isEmpty( this.params?.AD?.SETTINGS ) ||
			!this.utils.Type.isObject( this.params?.AD?.SETTINGS )
		) {
			this?.logger?.log( 'mergeParamsAdSettings', 'params.AD.SETTINGS is unset/invalid... returning resource unmodified' );
			return resource;
		} else {
			this?.logger?.log( 'mergeParamsAdSettings', 'params.AD.SETTINGS is set... deep merging into resource.ad', '\nparams.AD.SETTINGS:', this.params?.AD?.SETTINGS );
			// merge ad settings passed from page into our resource.ad bucket
			resource.ad = this.utils.DeepMerge.deepmerge( resource.ad, this.params?.AD?.SETTINGS );
			return resource;
		}
	}
}